import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../shared/api/client';
import Toast from '../../components/Toast';
import { CreditCard, BarChart2, Info } from 'react-feather';

interface Integration {
  id: number;
  bank_name: string;
  sandbox_mode: boolean;
  consent_status: string;
  last_sync_at?: string;
  user_id: number;
  account_count?: number;
  transaction_count?: number;
}

interface Account {
  id: number;
  integration_id: number;
  account_number: string;
  account_name: string;
  account_type: string;
  balance: number;
  currency: string;
  last_sync_at: string;
}

interface Institution {
  id: string;
  name: string;
  logo?: string;
}

// The server knows WHY the bank refused (rate limit, closed session). Losing
// that on the way to the toast leaves the user with nothing to act on.
const errorText = (error: any, fallback: string): string =>
  error?.response?.data?.error || error?.error || fallback;

const BankIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' as 'success' | 'error' | 'info' });

  // Bank picker (GoCardless institutions)
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [institutionsLoading, setInstitutionsLoading] = useState(false);
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ visible: true, message, type });
  };

  const hideToast = () => {
    setToast({ visible: false, message: '', type: 'success' });
  };

  useEffect(() => {
    if (user) {
      loadIntegrations();
      loadAccounts();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Handle redirect back from the bank (GoCardless callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === '1') {
      showToast('Banken är ansluten! Klicka "Synka transaktioner" för att hämta dina transaktioner.', 'success');
    } else if (params.get('error')) {
      const errorMessages: { [key: string]: string } = {
        bank_rejected: 'Banken avvisade anslutningen',
        not_linked: 'Anslutningen godkändes inte - försök igen',
        callback_failed: 'Något gick fel när anslutningen skulle slutföras',
        missing_ref: 'Ogiltig återkoppling från banken',
        unknown_ref: 'Anslutningen kunde inte matchas - försök igen',
      };
      showToast(errorMessages[params.get('error')!] || 'Bankanslutningen misslyckades', 'error');
    }
    if (params.get('connected') || params.get('error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIntegrations = async () => {
    try {
      const response = await apiClient.get('/bank/integrations');
      if (response.success) {
        const integrations = (response as any).integrations || response.data?.integrations || [];
        setIntegrations(integrations);
      }
    } catch (error) {
      console.error('Load integrations error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await apiClient.get('/bank/accounts');
      if (response.success) {
        const accounts = (response as any).accounts || response.data?.accounts || [];
        setAccounts(accounts);
      }
    } catch (error) {
      console.error('Load accounts error:', error);
    }
  };

  const loadTransactions = async (accountId: number) => {
    try {
      const response = await apiClient.get(`/bank/transactions?accountId=${accountId}&limit=20`);
      if (response.success) {
        setTransactions((response as any).transactions || response.data?.transactions || []);
        setShowTransactionsModal(true);
      }
    } catch (error) {
      console.error('Load transactions error:', error);
    }
  };

  const openAddModal = async () => {
    setShowAddModal(true);
    if (institutions.length > 0) return;
    setInstitutionsLoading(true);
    try {
      const response = await apiClient.get('/bank/institutions');
      setInstitutions((response as any).institutions || []);
    } catch (error: any) {
      console.error('Load institutions error:', error);
      const serverError = error?.response?.data?.error;
      showToast(serverError || 'Kunde inte hämta banklistan', 'error');
      setShowAddModal(false);
    } finally {
      setInstitutionsLoading(false);
    }
  };

  const handleConnect = async (institution: Institution) => {
    setAdding(true);
    try {
      const response = await apiClient.post('/bank/connect', {
        institutionId: institution.id,
        institutionName: institution.name
      });
      const link = (response as any).link;
      if (response.success && link) {
        // Off to the bank (BankID) to approve read access
        window.location.href = link;
      } else {
        showToast(response.error || 'Kunde inte starta bankanslutningen', 'error');
        setAdding(false);
      }
    } catch (error: any) {
      console.error('Connect error:', error);
      const serverError = error?.response?.data?.error;
      showToast(serverError || 'Kunde inte starta bankanslutningen', 'error');
      setAdding(false);
    }
  };

  const handleDeleteIntegration = async (id: number, bankName: string) => {
    if (window.confirm(`Är du säker på att du vill ta bort ${bankName}?`)) {
      try {
        const response = await apiClient.delete(`/bank/integrations/${id}`);
        if (response.success) {
          showToast('Integration borttagen', 'success');
          loadIntegrations();
          loadAccounts();
        } else {
          showToast(response.error || 'Kunde inte ta bort integration', 'error');
        }
      } catch (error) {
        console.error('Delete integration error:', error);
        showToast('Kunde inte ta bort integration', 'error');
      }
    }
  };

  const handleSyncAccounts = async (id: number, bankName: string) => {
    setSyncing(true);
    try {
      const response = await apiClient.post(`/bank/integrations/${id}/sync-accounts`);
      if (response.success) {
        showToast(response.data?.message || 'Konton synkade', 'success');
        loadAccounts();
        loadIntegrations();
      } else {
        showToast(response.error || 'Kunde inte synka konton', 'error');
      }
    } catch (error) {
      console.error('Sync accounts error:', error);
      showToast(errorText(error, 'Kunde inte synka konton'), 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncTransactions = async (accountId: number, accountName: string) => {
    setSyncing(true);
    try {
      const account = accounts.find(a => a.id === accountId);
      if (!account) return;

      const response = await apiClient.post(`/bank/integrations/${account.integration_id}/sync-transactions`, {
        accountId: account.id
      });

      if (response.success) {
        showToast(response.data?.message || 'Transaktioner synkade', 'success');
        loadIntegrations();
      } else {
        showToast(response.error || 'Kunde inte synka transaktioner', 'error');
      }
    } catch (error) {
      console.error('Sync transactions error:', error);
      showToast(errorText(error, 'Kunde inte synka transaktioner'), 'error');
    } finally {
      setSyncing(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'connected': '#10B981',
      'ACTIVE': '#10B981',
      'PENDING': '#F59E0B',
      'FAILED': '#EF4444',
      'EXPIRED': '#6B7280',
      'REVOKED': '#6B7280'
    };
    return colors[status] || '#6B7280';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Toast
          message={toast.message}
          type={toast.type}
          visible={toast.visible}
          onHide={hideToast}
        />
        <div className="page-header bg-white shadow-sm border-b border-gray-200">
          <div className="page-header-content px-4 py-4">
            <div className="flex items-center">
              <button onClick={() => navigate('/settings')} className="mr-4">
                <span className="text-xl">‹</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Bankintegration</h1>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onHide={hideToast}
      />
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings')} className="mr-4">
              <span className="text-xl">‹</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Bankintegration</h1>
          </div>
        </div>
      </div>

      <div className="page-content px-4 py-6">
        <p className="text-gray-600 mb-6">Anslut din bank för automatisk import av transaktioner</p>

        {/* Add Integration Button */}
        <button
          className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold mb-6 hover:bg-blue-700 transition-colors"
          onClick={openAddModal}
        >
          + Lägg till bank
        </button>

        {/* Integrations List */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Anslutningar</h2>
          {integrations.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <CreditCard size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga bankanslutningar ännu</h3>
              <p className="text-gray-600">Klicka på "Lägg till bank" för att komma igång</p>
            </div>
          ) : (
            <div className="space-y-3">
              {integrations.map(integration => (
                <div key={integration.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <span className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <CreditCard size={20} className="text-blue-500" />
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{integration.bank_name}</h3>
                      <p className="text-sm text-gray-600">
                        {integration.sandbox_mode ? 'Sandbox' : 'Live'}
                      </p>
                    </div>
                  </div>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: getStatusColor(integration.consent_status) }}
                  >
                    {integration.consent_status}
                  </span>
                  </div>

                  <div className="flex gap-4 mb-3 text-sm text-gray-600">
                    <span>{integration.account_count || 0} konto{(integration.account_count || 0) !== 1 ? 'n' : ''}</span>
                    <span>Bankintegration aktiv</span>
                  </div>

                  {integration.last_sync_at && (
                    <p className="text-xs text-gray-500 mb-3">
                      Senast synkad: {formatDate(integration.last_sync_at)}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                      onClick={() => handleSyncAccounts(integration.id, integration.bank_name)}
                      disabled={syncing}
                    >
                      Synka konton
                    </button>
                    <button
                      className="bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                      onClick={() => handleDeleteIntegration(integration.id, integration.bank_name)}
                    >
                      Ta bort
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Accounts List */}
        {accounts.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Konton</h2>
            <div className="space-y-3">
              {accounts.map(account => (
                <div key={account.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{account.account_name}</h3>
                    <span className="text-lg font-semibold text-green-600">{formatAmount(account.balance)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    {account.account_number} • {account.account_type}
                  </p>
                  <p className="text-xs text-gray-500 font-mono mb-3">
                    Senast uppdaterad: {formatDate(account.last_sync_at)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                      onClick={() => handleSyncTransactions(account.id, account.account_name)}
                      disabled={syncing}
                    >
                      Synka transaktioner
                    </button>
                    <button
                      className="bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors"
                      onClick={() => {
                        setSelectedAccount(account);
                        loadTransactions(account.id);
                      }}
                    >
                      Visa transaktioner
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Integration Modal - pick a bank, then approve at the bank via GoCardless */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[80vh] flex flex-col">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Välj din bank</h2>

            <input
              type="text"
              placeholder="Sök bank…"
              value={institutionSearch}
              onChange={e => setInstitutionSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3"
            />

            <div className="flex-1 overflow-y-auto mb-4 min-h-[200px]">
              {institutionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                institutions
                  .filter(i => i.name.toLowerCase().includes(institutionSearch.toLowerCase()))
                  .map(institution => (
                    <button
                      key={institution.id}
                      className="w-full flex items-center p-3 rounded-lg hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                      onClick={() => handleConnect(institution)}
                      disabled={adding}
                    >
                      {institution.logo ? (
                        <img src={institution.logo} alt="" className="w-8 h-8 mr-3 rounded" />
                      ) : (
                        <CreditCard size={20} className="text-gray-400 mr-3" />
                      )}
                      <span className="font-medium text-gray-900">{institution.name}</span>
                    </button>
                  ))
              )}
              {!institutionsLoading && institutions.length > 0 &&
                institutions.filter(i => i.name.toLowerCase().includes(institutionSearch.toLowerCase())).length === 0 && (
                <p className="text-center text-gray-500 py-8">Ingen bank matchar sökningen</p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <Info size={13} className="inline mr-1 -mt-0.5" /> Du skickas till din bank för att godkänna läsåtkomst med BankID. Åtkomsten gäller i 90 dagar.
              </p>
            </div>

            <button
              className="w-full bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
              onClick={() => setShowAddModal(false)}
              disabled={adding}
            >
              {adding ? 'Skickar dig till banken…' : 'Avbryt'}
            </button>
          </div>
        </div>
      )}

      {/* Transactions Modal */}
      {showTransactionsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-hidden">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Transaktioner - {selectedAccount.account_name}
            </h2>
            <div className="max-h-96 overflow-y-auto">
              {transactions.map(tx => (
                <div key={tx.id} className="border-b border-gray-100 py-3 last:border-b-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{tx.description}</p>
                      <p className="text-sm text-gray-600">{formatDate(tx.transaction_date)}</p>
                      {tx.counterparty_name && (
                        <p className="text-sm text-gray-500">{tx.counterparty_name}</p>
                      )}
                      {tx.bank_reference && (
                        <p className="text-xs text-gray-400 font-mono">{tx.bank_reference}</p>
                      )}
                    </div>
                    <span className={`font-semibold ${tx.transaction_type === 'INCOME' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatAmount(tx.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold mt-4 hover:bg-blue-700 transition-colors"
              onClick={() => setShowTransactionsModal(false)}
            >
              Stäng
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankIntegrationPage;
